import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { formatGMV } from 'lib/utils/FormatUtils';
import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_XS,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  SHADOW_XS,
  SKELETON_BG,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * Compact stat row used inside each card. Renders nothing if value is null/undefined.
 */
function StatRow({ label, value }) {
  if (value === null || value === undefined) return null;
  return (
    <div className={css(styles.stat)}>
      <span className={css(styles.statLabel)}>{label}</span>
      <span className={css(styles.statValue)}>{value}</span>
    </div>
  );
}

StatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

StatRow.defaultProps = { value: null };

// Maps skuType (matches the existing funnel SKU_TYPES) → card stat label + the
// field on the stats row that drives the value. Each skuType reads its own
// backend column: active_skus (gpv.active_status = 'Active') and gmv_skus
// (monetizable GMV, plc.MONITIZABLE_GMV > 0) are distinct counts.
const SKU_TYPE_TO_STAT = {
  total: { label: 'Total SKUs', field: 'total_skus' },
  active: { label: 'Active SKUs', field: 'active_skus' },
  gmv: { label: 'GMV SKUs', field: 'gmv_skus' },
};

/**
 * CategoryCard — a single category tile (image + name + stat block).
 */
function CategoryCard({ category, stats, skuMeta, onCardClick }) {
  const label = category.taxonomy_display_name || category.category_name || '';
  const imageUrl = category.taxonomy_image || placeholderProductImage;
  const gmv = stats ? formatGMV(stats.annualized_gmv) : null;
  const skuRaw = stats ? stats[skuMeta.field] : null;
  const skuCount = skuRaw != null ? Number(skuRaw).toLocaleString('en-US') : null;
  const dps =
    stats && stats.dp_count != null ? Number(stats.dp_count).toLocaleString('en-US') : null;

  return (
    <button
      type="button"
      className={css(styles.card)}
      onClick={() => onCardClick && onCardClick(category)}
      title={label}
    >
      <div className={css(styles.imageBox)}>
        <img src={imageUrl} alt="" className={css(styles.image)} />
      </div>
      <div className={css(styles.body)}>
        <div className={css(styles.name)}>{label}</div>
        <div className={css(styles.stats)}>
          <StatRow label="Annualized GMV" value={gmv} />
          <StatRow label={skuMeta.label} value={skuCount} />
          <StatRow label="DPs" value={dps} />
        </div>
      </div>
    </button>
  );
}

CategoryCard.propTypes = {
  category: PropTypes.object.isRequired,
  stats: PropTypes.object,
  skuMeta: PropTypes.shape({ label: PropTypes.string, field: PropTypes.string }).isRequired,
  onCardClick: PropTypes.func,
};

CategoryCard.defaultProps = {
  stats: null,
  onCardClick: null,
};

/**
 * BetaCategoryGrid — beta-styled card grid matching the mockup
 * (UPB Viewer - Category.html). Each card shows a large image area
 * at top followed by the category name and per-card stats:
 * Annualized GMV, <Active|Total|GMV> SKUs, DPs.
 *
 * Props:
 *   categories  Array<{ id, category_name, taxonomy_display_name, taxonomy_image }>
 *   statsById   { [id]: { annualized_gmv, total_skus, active_skus, gmv_skus, dp_count } }
 *   loading     boolean — show skeleton tiles
 *   onCardClick (category) => void
 *   skuType     'total' | 'active' | 'gmv' — switches the SKU stat shown on each card
 */
function BetaCategoryGrid({ categories, statsById, loading, onCardClick, skuType }) {
  const skuMeta = SKU_TYPE_TO_STAT[skuType] || SKU_TYPE_TO_STAT.active;
  if (loading) {
    return (
      <div className={css(styles.grid)}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
          <div key={n} className={css(styles.skeleton)} />
        ))}
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return <div className={css(styles.empty)}>No categories to show.</div>;
  }

  return (
    <div className={css(styles.grid)}>
      {categories.map(cat => (
        <CategoryCard
          key={cat.id}
          category={cat}
          stats={(statsById && statsById[cat.id]) || null}
          skuMeta={skuMeta}
          onCardClick={onCardClick}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
    gap: 14,
  },

  card: {
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
    ':hover': {
      borderColor: PRIMARY_COLOR,
      boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}, ${SHADOW_XS}`,
    },
  },

  imageBox: {
    // Mockup shows a roughly-square image area on category cards. With
    // ~200px wide cards (after the 1fr stretch), 180px tall gives a near
    // 1:1 look. Fixed height + overflow hidden — same robust pattern used
    // on the product card image area.
    height: 180,
    overflow: 'hidden',
    backgroundColor: BG_2,
    borderBottom: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    flexShrink: 0,
  },

  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },

  body: {
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },

  name: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    lineHeight: 1.3,
    textTransform: 'capitalize',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    borderTop: `1px solid ${BORDER_2}`,
    paddingTop: 8,
  },

  stat: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },

  statLabel: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
  },

  statValue: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: FONT_SIZE_XS,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },

  skeleton: {
    height: 210,
    borderRadius: RADIUS_LG,
    backgroundColor: SKELETON_BG,
    animationName: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },

  empty: {
    padding: 32,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_BODY,
    color: FG_3,
    textAlign: 'center',
  },
});

BetaCategoryGrid.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object),
  statsById: PropTypes.object,
  loading: PropTypes.bool,
  onCardClick: PropTypes.func,
  skuType: PropTypes.oneOf(['total', 'active', 'gmv']),
};

BetaCategoryGrid.defaultProps = {
  categories: [],
  statsById: {},
  loading: false,
  onCardClick: null,
  skuType: 'active',
};

export default BetaCategoryGrid;
